import React from 'react';
import {
  Box,
  Drawer,
  Typography,
  IconButton,
  Stack
} from '@mui/material';
import {
  Close as CloseIcon
} from '@mui/icons-material';

// SAP Fiori Color Palette - Modern Refresh
const FIORI_COLORS = {
  // Primary colors
  primary: '#0A6ED1',
  primaryHover: '#085BAB',
  primaryLight: '#E8F0F8',

  // Background colors
  background: '#F7F8F9',
  surface: '#FFFFFF',
  headerBg: '#FFFFFF',

  // Text colors
  textPrimary: '#32363A',
  textSecondary: '#6A6D70',
  textTertiary: '#9DA0A5',

  // Border colors
  border: '#E0E0E0',
  borderLight: '#F0F0F0',

  // Status colors
  success: '#107E3E',
  warning: '#E9730C',
  error: '#BB0000',
  info: '#0A6ED1'
};

export interface FioriSidePanelProps {
  open: boolean;
  onClose: () => void;
  title: string;
  icon?: React.ReactNode;
  subtitle?: string;
  children: React.ReactNode;
  actions?: React.ReactNode;
  width?: number | string;
  anchor?: 'right' | 'left' | 'top' | 'bottom';
}

const FioriSidePanel: React.FC<FioriSidePanelProps> = ({
  open,
  onClose,
  title,
  icon,
  subtitle,
  children,
  actions,
  width = 720,
  anchor = 'right'
}) => {
  return (
    <Drawer
      anchor={anchor}
      open={open}
      onClose={onClose}
      sx={{
        '& .MuiDrawer-paper': {
          width: width,
          maxWidth: '95vw',
          backgroundColor: FIORI_COLORS.surface,
          borderLeft: anchor === 'right' ? `1px solid ${FIORI_COLORS.border}` : 'none',
          borderRight: anchor === 'left' ? `1px solid ${FIORI_COLORS.border}` : 'none',
          boxShadow: anchor === 'right' ? '-4px 0 24px rgba(0,0,0,0.08)' : '4px 0 24px rgba(0,0,0,0.08)'
        }
      }}
      SlideProps={{
        easing: {
          enter: 'cubic-bezier(0.25, 0.46, 0.45, 0.94)',
          exit: 'cubic-bezier(0.25, 0.46, 0.45, 0.94)'
        }
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100vh',
          backgroundColor: FIORI_COLORS.surface
        }}
      >
        {/* Header - Compact & Sticky */}
        <Box
          sx={{
            backgroundColor: FIORI_COLORS.headerBg,
            borderBottom: `1px solid ${FIORI_COLORS.border}`,
            px: 1.5,
            py: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexShrink: 0,
            height: '52px',
            position: 'sticky',
            top: 0,
            zIndex: 10,
            boxShadow: '0 1px 3px rgba(0,0,0,0.04)'
          }}
        >
          <Stack direction="row" alignItems="center" spacing={0.75} sx={{ flex: 1, minWidth: 0 }}>
            {icon && (
              <Box sx={{
                color: FIORI_COLORS.primary,
                display: 'flex',
                alignItems: 'center',
                fontSize: '1.25rem'
              }}>
                {icon}
              </Box>
            )}
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                variant="body1"
                component="div"
                sx={{
                  fontWeight: 600,
                  color: FIORI_COLORS.textPrimary,
                  fontSize: '0.9375rem',
                  lineHeight: 1.3,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}
              >
                {title}
              </Typography>
              {subtitle && (
                <Typography
                  variant="caption"
                  sx={{
                    color: FIORI_COLORS.textSecondary,
                    fontSize: '0.75rem',
                    lineHeight: 1.2,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: 'block'
                  }}
                >
                  {subtitle}
                </Typography>
              )}
            </Box>
          </Stack>
          <IconButton
            onClick={onClose}
            size="small"
            sx={{
              color: FIORI_COLORS.textSecondary,
              ml: 0.5,
              '&:hover': {
                backgroundColor: FIORI_COLORS.primaryLight,
                color: FIORI_COLORS.primary
              },
              borderRadius: 1,
              p: 0.5,
              fontSize: '1.25rem'
            }}
          >
            <CloseIcon sx={{ fontSize: '1.25rem' }} />
          </IconButton>
        </Box>

        {/* Content */}
        <Box
          sx={{
            flex: 1,
            overflow: 'auto',
            px: 1.5,
            py: 0,
            backgroundColor: FIORI_COLORS.background,
            '&::-webkit-scrollbar': {
              width: '8px',
            },
            '&::-webkit-scrollbar-track': {
              background: FIORI_COLORS.borderLight,
            },
            '&::-webkit-scrollbar-thumb': {
              background: FIORI_COLORS.border,
              borderRadius: '4px',
              '&:hover': {
                background: FIORI_COLORS.textSecondary,
              }
            }
          }}
        >
          <Box sx={{ py: 1 }}>
            {children}
          </Box>
        </Box>

        {/* Footer Actions - Fixed at bottom */}
        {actions && (
          <Box
            sx={{
              backgroundColor: FIORI_COLORS.surface,
              borderTop: `1px solid ${FIORI_COLORS.border}`,
              px: 1.5,
              py: 1,
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 0.5,
              flexShrink: 0,
              boxShadow: '0 -2px 8px rgba(0,0,0,0.04)',
              position: 'sticky',
              bottom: 0,
              zIndex: 10
            }}
          >
            {actions}
          </Box>
        )}
      </Box>
    </Drawer>
  );
};

export default FioriSidePanel;
export { FIORI_COLORS };
